import Image from 'app/Components/Shared/Image/image';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Input, ListGroup, ListGroupItem } from 'reactstrap';
import ArrowIcon from './arrow.svg';

const SelectWithCheckboxes = ({
  options = {},
  onSelectionChange,
  name,
  placeholder = 'בחר אפשרויות',
  disabled = false,
  required = false,
  error = '',
  selectedValues = [],
  maxSelections = null,
  showSelectAll = true,
  showClearAll = true
}) => {
  const [showOptions, setShowOptions] = useState(false);
  const [internalSelectedValues, setInternalSelectedValues] = useState(selectedValues);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(options);

  const selectRef = useRef();
  const dropdownRef = useRef();

  // Convert options object to array for easier handling
  const optionsArray = Object.entries(options).map(([key, value]) => ({ key, value }));

  useEffect(() => {
    setInternalSelectedValues(selectedValues);
  }, [selectedValues]);

  useEffect(() => {
    setFilteredOptions(options);
  }, [options]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowOptions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleDropdown = useCallback(() => {
    if (!disabled) {
      setShowOptions(prev => !prev);
    }
  }, [disabled]);

  const handleOptionToggle = useCallback((optionKey) => {
    const isSelected = internalSelectedValues.includes(optionKey);

    let newSelectedValues;
    if (isSelected) {
      newSelectedValues = internalSelectedValues.filter(value => value !== optionKey);
    } else {
      if (maxSelections && internalSelectedValues.length >= maxSelections) {
        return; 
      }
      newSelectedValues = [...internalSelectedValues, optionKey];
    }

    setInternalSelectedValues(newSelectedValues);
    onSelectionChange(newSelectedValues);
  }, [internalSelectedValues, maxSelections, onSelectionChange]);

  const handleSelectAll = useCallback(() => {
    let newSelectedValues;
    const availableOptions = Object.keys(options);
    if (maxSelections) {
      newSelectedValues = availableOptions.slice(0, maxSelections);
    } else {
      newSelectedValues = availableOptions;
    }

    setInternalSelectedValues(newSelectedValues);
    onSelectionChange(newSelectedValues);
  }, [options, maxSelections, onSelectionChange]);

  const handleClearAll = useCallback(() => {
    setInternalSelectedValues([]);
    onSelectionChange([]);
  }, [onSelectionChange]);

  const getDisplayText = useCallback(() => {
    if (internalSelectedValues.length === 0) {
      return '';
    }
    if (internalSelectedValues.length === 1) {
      const selectedKey = internalSelectedValues[0];
      return options[selectedKey] || selectedKey;
    }
    return `${internalSelectedValues.length} נבחרו`;
  }, [internalSelectedValues, options]);

  // Styling logic
  const formControl = error && error.length > 0 && internalSelectedValues.length > 0 
    ? 'input-error' 
    : 'form-control';

  const isMaxReached = maxSelections && internalSelectedValues.length >= maxSelections;

  return (
    <div className='select-checkbox-container' ref={dropdownRef}>
      <div className='select-checkbox-input'>
        <label className={`has-float-${required ? 'label-required' : 'label'}`}>
          <div className='input-with-arrow'>
            <Image src={ArrowIcon} 
              alt="arrow" 
              className={`arrow-icon ${showOptions ? 'rotated' : ''}`} />
            <Input
              innerRef={selectRef}
              className={formControl}
              name={name}
              placeholder={placeholder}
              disabled={disabled}
              type='text'
              autoComplete='off'
              value={getDisplayText()}
              onClick={toggleDropdown}
              readOnly
            />
          </div>
          <span>{placeholder}</span>
          {error && error.length > 0 && <div className='error'>{error}</div>}
        </label>

        {showOptions && (
          <div className='checkbox-options'>
            <div className='action-buttons'>
              {showSelectAll && (
                <button
                  type='button'
                  className='action-btn select-all-btn'
                  onClick={handleSelectAll}
                  disabled={isMaxReached}
                >
                  בחר הכל
                </button>
              )}
              {showClearAll && (
                <button
                  type='button'
                  className='action-btn clear-all-btn'
                  onClick={handleClearAll}
                >
                  נקה הכל
                </button>
              )}
            </div>

            <ListGroup className='options-list'>
              {optionsArray.map(({ key, value }) => {
                const isSelected = internalSelectedValues.includes(key);
                const isDisabled = !isSelected && isMaxReached;

                return (
                  <ListGroupItem
                    key={key}
                    className={`option-item ${isSelected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
                    onClick={() => !isDisabled && handleOptionToggle(key)}
                  >
                    <div className='checkbox-wrapper'>
                      <input
                        type='checkbox'
                        checked={isSelected}
                        disabled={isDisabled}
                      />
                      <span className='option-text'>{value}</span>
                    </div>
                  </ListGroupItem>
                );
              })}
            </ListGroup>

            {optionsArray.length === 0 && (
              <div className='no-options'>לא נמצאו תוצאות</div>
            )}
          </div>
        )}

        {/* Hidden inputs for form submission */}
        {internalSelectedValues.map((value, index) => (
          <Input
            key={index}
            type='hidden'
            name={`${name}[${index}]`}
            value={value}
          />
        ))}
      </div>
    </div>
  );
};

SelectWithCheckboxes.propTypes = {
  options: PropTypes.object,
  onSelectionChange: PropTypes.func.isRequired,
  name: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  error: PropTypes.string,
  selectedValues: PropTypes.array,
  maxSelections: PropTypes.number,
  showSelectAll: PropTypes.bool,
  showClearAll: PropTypes.bool
};

export default SelectWithCheckboxes;